import { useGlobalSetupStore } from "@/utils/GlobalSetupStore";
import { useLocationStore } from "@/utils/LocationStore";
import { pb } from "@/utils/PocketBaseAdapter";
import { moment } from "@/utils/useTimeZone";
import { computed, ref } from "vue";

type TermsType = "general" | "membership";

interface QueueItem {
  type: TermsType;
  title: string;
  htmlContent: string;
  notice: string;
  acceptedField: "acceptedTCs" | "acceptedMembersTCs";
}

const queue = ref<QueueItem[]>([]);

// Only run the check once per login, not on every navigation. Reset back to
// false whenever the auth store is cleared (logout), so the next login
// checks again
let hasCheckedThisLogin = false;
pb.authStore.onChange(() => {
  if (!pb.authStore.isValid) hasCheckedThisLogin = false;
});

const needsReacceptance = (acceptedDate?: string, editedDate?: string) => {
  if (!editedDate) return false;
  if (!acceptedDate) return true;
  return moment(acceptedDate).isBefore(moment(editedDate));
};

export function useTermsReacceptance() {
  const globalSetupStore = useGlobalSetupStore();
  const { globalSetup } = storeToRefs(globalSetupStore);

  const showDialog = computed(() => queue.value.length > 0);
  const current = computed<QueueItem | null>(() => queue.value[0] || null);

  const checkTermsReacceptance = () => {
    const user = pb.authStore?.model;
    if (!pb.authStore?.isValid || !user?.id) return;

    // Accounts part-way through OAuth signup are asked to accept the terms by
    // the register wizard, so they have nothing to re-accept yet.
    if (user.status === "ExtAuth") return;

    if (hasCheckedThisLogin) return;
    if (queue.value.length) return; // Already showing something
    hasCheckedThisLogin = true;

    const pending: QueueItem[] = [];

    if (needsReacceptance(user.acceptedTCs, globalSetup.value.generalTermsAndConditionsEditedDate)) {
      pending.push({
        type: "general",
        title: "Terms & Conditions",
        htmlContent: globalSetup.value.generalTermsAndConditions || "",
        notice: "Our Terms & Conditions have changed. Please review and accept them to keep using your account.",
        acceptedField: "acceptedTCs",
      });
    }

    // Membership terms only apply to users who actually hold a membership
    if (
      user.membership &&
      needsReacceptance(user.acceptedMembersTCs, globalSetup.value.membershipTermsAndConditionsEditedDate)
    ) {
      pending.push({
        type: "membership",
        title: "Membership Terms & Conditions",
        htmlContent: globalSetup.value.membershipTermsAndConditions || "",
        notice: "Our Membership Terms & Conditions have changed. Please review and accept them to keep your membership benefits.",
        acceptedField: "acceptedMembersTCs",
      });
    }

    if (pending.length) queue.value = pending;
  };

  const resolveCurrent = () => {
    queue.value = queue.value.slice(1);
  };

  const acceptCurrent = async () => {
    const item = current.value;
    const user = pb.authStore?.model;
    if (!item || !user?.id) return;
    try {
      const updates: Record<string, any> = { [item.acceptedField]: moment().toISOString() };

      // Declining terms is the only thing this flow puts on hold. If the
      // user is still on hold, only lift it if it's not also being held for
      // failed payments (per the location's failedPaymentStopBenefitsCount)
      // - that's a separate, unrelated reason and must stay in place.
      if (user.preventBenefits) {
        const location = useLocationStore().locations.get(user.homeLocation);
        const stopBenefitsThreshold = location?.failedPaymentStopBenefitsCount;
        const failedPayments = user.failedPayments || 0;
        const heldForFailedPayments = !!stopBenefitsThreshold && failedPayments >= stopBenefitsThreshold;

        if (!heldForFailedPayments) {
          updates.preventBenefits = false;
        }
      }

      await pb.collection("users").update(user.id, updates);
      await pb.collection("users").authRefresh();
    } catch (e) {
      console.error("Failed to save terms acceptance:", e);
    } finally {
      resolveCurrent();
    }
  };

  const declineCurrent = async () => {
    const item = current.value;
    const userId = pb.authStore?.model?.id;
    if (!item || !userId) return;
    try {
      await pb.collection("users").update(userId, { preventBenefits: true });
      await pb.collection("users").authRefresh();
    } catch (e) {
      console.error("Failed to record terms decline:", e);
    } finally {
      resolveCurrent();
    }
  };

  return { showDialog, current, checkTermsReacceptance, acceptCurrent, declineCurrent };
}
